import type { Auth } from 'firebase/auth'
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  where,
  writeBatch,
  type DocumentReference,
  type Firestore,
} from 'firebase/firestore'
import {
  assertActiveLedger,
  getUserLedgerRef,
} from '../services/ledgerService'
import { deleteMemory, flushMemory } from '../services/memoryService'
import { deleteMyTeamData, requestAccountDeletion } from '../services/accountDeletionService'
import { requestWorkspaceDeletion } from '../services/workspaceDeletionService'
import type { Workspace, WorkspaceKind, WorkspaceRole } from '../types/workspace'
import { isPersonalWorkspace } from './workspaceKind'
import { parseNonHelperRole } from './workspacePermissions'

const FIRESTORE_BATCH_LIMIT = 450
const DEFAULT_WORKSPACE_CREDITS = 0

type WorkspaceRecord = {
  id: string
  name: string
  ownerId: string
  credits: number
  isPersonal: boolean
  kind: WorkspaceKind | undefined
}

type MembershipRecord = {
  id: string
  workspaceId: string
  userId: string
  role: Exclude<WorkspaceRole, 'HELPER'>
  credits: number
}

const commitDeletesInBatches = async (db: Firestore, refs: DocumentReference[]) => {
  for (let index = 0; index < refs.length; index += FIRESTORE_BATCH_LIMIT) {
    const batch = writeBatch(db)
    const chunk = refs.slice(index, index + FIRESTORE_BATCH_LIMIT)

    for (const ref of chunk) {
      batch.delete(ref)
    }

    await batch.commit()
  }
}

const loadWorkspace = async (db: Firestore, workspaceId: string): Promise<WorkspaceRecord | null> => {
  const snapshot = await getDoc(doc(db, 'workspaces', workspaceId))
  if (!snapshot.exists()) return null

  const data = snapshot.data()
  // eslint-disable-next-line no-restricted-syntax -- writer normalizing the raw `kind` enum value itself (not an isPersonal predicate); isPersonalWorkspace() returns boolean and cannot replace this whitelist check.
  const kind: WorkspaceKind | undefined = data.kind === 'PERSONAL' || data.kind === 'TEAM' ? data.kind : undefined
  return {
    id: snapshot.id,
    name: typeof data.name === 'string' ? data.name : 'Untitled workspace',
    ownerId: typeof data.ownerId === 'string' ? data.ownerId : '',
    credits: typeof data.credits === 'number' ? data.credits : DEFAULT_WORKSPACE_CREDITS,
    isPersonal: isPersonalWorkspace(data as Pick<Workspace, 'isPersonal' | 'kind'>),
    kind,
  }
}

const loadUserMemberships = async (db: Firestore, userId: string): Promise<MembershipRecord[]> => {
  const snapshots = await Promise.all([
    getDocs(query(collection(db, 'workspace_members'), where('userId', '==', userId))),
    getDocs(query(collection(db, 'workspace_members'), where('uid', '==', userId))),
  ])
  const documents = Array.from(
    new Map(snapshots.flatMap((snapshot) => snapshot.docs).map((snapshot) => [snapshot.id, snapshot])).values(),
  )

  return documents.map((membershipDocument) => {
    const data = membershipDocument.data()
    return {
      id: membershipDocument.id,
      workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : '',
      userId,
      role: parseNonHelperRole(data.role) ?? 'VIEWER',
      credits: typeof data.credits === 'number' ? data.credits : 0,
    }
  }).filter((membership) => membership.workspaceId && membership.id && membership.role)
}

const deleteElementsForWorkspace = async (db: Firestore, workspaceId: string) => {
  const elementsSnapshot = await getDocs(
    query(collection(db, 'elements'), where('workspaceId', '==', workspaceId)),
  )

  await commitDeletesInBatches(db, elementsSnapshot.docs.map((snapshot) => snapshot.ref))
}

const collapseOwnedTeamWorkspace = async (
  workspace: WorkspaceRecord,
) => {
  return requestWorkspaceDeletion({ workspaceId: workspace.id })
}

export const deleteWorkspaceData = async (workspaceId: string) => {
  return requestWorkspaceDeletion({ workspaceId })
}

export const deletePersonalData = async (userId: string, email: string, db: Firestore) => {
  await assertActiveLedger(db, email)

  const memberships = await loadUserMemberships(db, userId)
  const workspaces = await Promise.all(memberships.map((membership) => loadWorkspace(db, membership.workspaceId)))
  const personalWorkspaces = workspaces.filter((workspace): workspace is WorkspaceRecord =>
    isPersonalWorkspace(workspace),
  )

  for (const workspace of personalWorkspaces) {
    await deleteElementsForWorkspace(db, workspace.id)
  }
}

export const deleteTeamData = async (
  userId: string,
  email: string,
  db: Firestore,
  activeWorkspaceId: string,
) => {
  await assertActiveLedger(db, email)

  const activeWorkspace = await loadWorkspace(db, activeWorkspaceId)
  if (!activeWorkspace || isPersonalWorkspace(activeWorkspace)) {
    throw new Error('Select a team workspace before deleting team data.')
  }

  if (activeWorkspace.ownerId === userId) {
    return collapseOwnedTeamWorkspace(activeWorkspace)
  }

  return deleteMyTeamData({ scope: 'ONE', workspaceId: activeWorkspaceId })
}

export const deleteAllTeamData = async (_userId: string, email: string, db: Firestore) => {
  await assertActiveLedger(db, email)
  return deleteMyTeamData({ scope: 'ALL' })
}

export const deleteAllData = async (userId: string, email: string, db: Firestore) => {
  await assertActiveLedger(db, email)
  await deletePersonalData(userId, email, db)
  await deleteMyTeamData({ scope: 'ALL' })

  await runTransaction(db, async (transaction) => {
    transaction.set(getUserLedgerRef(db, email), {
      status: 'DORMANT',
      lastActiveAt: serverTimestamp(),
    }, { merge: true })
  })
}

export const deleteUserAccount = async (userId: string, _db?: Firestore, _auth?: Auth) => {
  void _db
  void _auth
  await flushMemory(userId)
  await deleteMemory(userId)
  return requestAccountDeletion()
}
